import { Box, Checkbox, Form, Grid, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Button, F0DatePicker, F0Select } from '@factorialco/f0-react'
import { DatePickerValue, Input, NumberInput } from '@factorialco/f0-react/dist/experimental'
import {
  AtsGetJobPostingSettingsBasicInfoDocument,
  AtsJobCatalogRoleSelectorFragment,
  AtsJobPostingSettingsBasicInformationFragment,
  AtsJobPostingSettingsEnabledFeaturesFragment,
  AtsJobPostingSettingsLocationsFragment,
  AtsJobPostingSettingsTeamsFragment,
  AtsUpdateJobPostingBasicInfoDocument,
  AtsWorkplaceTypesEnum,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { countryName } from 'modules/core/lib/countryList'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { JobCatalogRoleSelector } from 'modules/ats/components/job_catalog/JobCatalogRoleSelector'
import { JobCatalogLinkedInfoAlert } from 'modules/ats/pages/JobPostingSettings/JobCatalogAlerts/LinkedInfoAlert'
import { SectionComponentLayout } from 'modules/ats/pages/JobPostingSettings/SectionComponentLayout'

import { BasicInformationSkeleton } from './BasicInformationSkeleton'

interface Props {
  jobId: string
}

export const BasicInformation = ({ jobId }: Props) => {
  const { company } = useCurrentSession()

  const { data, loading } = useQuery(AtsGetJobPostingSettingsBasicInfoDocument, {
    variables: {
      id: jobId,
      companyId: company.id,
    },
  })

  useEffect(() => {
    track(trackingEvents.ats.jobSettingsAccessBasicInformation)
  }, [])

  const job = data?.ats.jobPosting
  const locations = data?.locations.locations ?? []
  const teams = data?.teams.teams ?? []
  const enabledFeatures = data?.enabledFeatures.featuresConnection.nodes ?? []
  const jobCatalogRoles = data?.jobCatalog.treeNodesConnection.nodes ?? []

  if (loading || !job) {
    return <BasicInformationSkeleton />
  }

  return (
    <BasicInformationForm
      job={job}
      locations={locations}
      teams={teams}
      jobCatalogSerializedTree={data?.jobCatalog.treesConnection.nodes[0]?.serializedTree ?? ''}
      enabledFeatures={enabledFeatures}
      jobCatalogRoles={jobCatalogRoles}
    />
  )
}

interface UpdateJobPostingBasicInfoForm {
  allowInternalApply: boolean
  allowReferral: boolean
  title: string
  legalEntityId: string | null
  locationId: string | null
  teamId: string | null
  numberOfVacancies: number | null
  workplaceType: AtsWorkplaceTypesEnum | null
  city: string
  state: string
  country: string
  postalCode: string
  startDate: string | null
  jobCatalogNodeRolesUuid: string | null
  jobCatalogNodeLevelsUuid: string | null
}

interface BasicInformationFormProps {
  job: AtsJobPostingSettingsBasicInformationFragment
  locations: AtsJobPostingSettingsLocationsFragment[]
  teams: AtsJobPostingSettingsTeamsFragment[]
  jobCatalogSerializedTree: string
  enabledFeatures: AtsJobPostingSettingsEnabledFeaturesFragment[]
  jobCatalogRoles: AtsJobCatalogRoleSelectorFragment[]
}

const BasicInformationForm = ({
  job,
  locations,
  teams,
  jobCatalogSerializedTree,
  jobCatalogRoles,
  enabledFeatures,
}: BasicInformationFormProps) => {
  const { company } = useCurrentSession()

  const jobCatalogFeatureEnabled =
    enabledFeatures.find((feature) => feature.name === 'dev_ats_job_posting_revamp')?.enabled ??
    false

  // TODO: Implement the correct Policy when backend is ready and replace feature-flag: dev_ats_hiring_plan
  const isHiringPlanEnabled =
    enabledFeatures.find((feature) => feature.name === 'dev_ats_hiring_plan')?.enabled ?? false

  const [updateJobPosting] = useMutation(AtsUpdateJobPostingBasicInfoDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsJobPosting,
  })

  const form = useForm<UpdateJobPostingBasicInfoForm>({
    defaultValues: {
      allowInternalApply: job.allowInternalApply ?? false,
      allowReferral: job.allowReferral ?? false,
      title: job.title,
      legalEntityId: job.legalEntity?.id ?? null,
      teamId: job.team?.id || null,
      numberOfVacancies: job.numberOfVacancies ?? null,
      workplaceType: job.workplaceType ?? null,
      locationId: job.location?.id ?? null,
      city: job.location?.city ?? '',
      state: job.location?.state ?? '',
      country: job.location?.country ?? '',
      postalCode: job.location?.postalCode ?? '',
      startDate: job.startDate ?? null,
      jobCatalogNodeRolesUuid: job.jobCatalogNodeRolesUuid ?? null,
      jobCatalogNodeLevelsUuid: job.jobCatalogNodeLevelsUuid ?? null,
    },
    validate(values) {
      const errors: FormErrors<UpdateJobPostingBasicInfoForm> = {}

      if (values.title === '') {
        errors.title = i18n.t('common.validations.empty')
      }

      if (!values.locationId) {
        errors.locationId = i18n.t('common.validations.empty')
      }

      if (values.numberOfVacancies && values.numberOfVacancies <= 0) {
        errors.numberOfVacancies = i18n.t('ats.jobs.invalid_number_of_vacancies')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await updateJobPosting({
        variables: {
          id: job.id,
          allowInternalApply: values.allowInternalApply,
          allowReferral: values.allowReferral,
          title: values.title,
          teamId: values.teamId,
          legalEntityId: values.legalEntityId,
          numberOfVacancies: values.numberOfVacancies ?? null,
          workplaceType: values.workplaceType,
          locationId: values.locationId,
          startDate: values.startDate,
          jobCatalogNodeRolesUuid: values.jobCatalogNodeRolesUuid,
          jobCatalogNodeLevelsUuid: values.jobCatalogNodeLevelsUuid,
        },
      })

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    },
  })

  const onChangeLocation = (value: string) => {
    const locationData = locations.find((location) => location.id === value)
    if (!locationData) return

    form.setValues({
      ...form.values,
      locationId: value,
      city: locationData.city ?? '',
      state: locationData.state ?? '',
      country: locationData.country ?? '',
      postalCode: locationData.postalCode ?? '',
    })
  }

  const startDatePickerValue: DatePickerValue | undefined = (() => {
    if (!form.values.startDate) {
      return undefined
    }

    const parsedDate = stringToDate(form.values.startDate, 'backend')

    if (!parsedDate.isValid()) {
      return undefined
    }

    const normalizedDate = parsedDate.toDate()

    return {
      granularity: 'day' as const,
      value: {
        from: normalizedDate,
        to: normalizedDate,
      },
    }
  })()

  const handleStartDateChange = (date: DatePickerValue | undefined) => {
    const selectedDate = date?.value?.from

    if (!selectedDate) {
      form.setFieldValue('startDate', null)
      return
    }

    form.setFieldValue('startDate', i18nDateToString(selectedDate, 'backend'))
  }

  const showLinkedInformationAlert =
    jobCatalogFeatureEnabled && !!(job.jobCatalogNodeRolesUuid && job.jobCatalogNodeLevelsUuid)

  return (
    <SectionComponentLayout
      sidebar={
        showLinkedInformationAlert && (
          // To DO: replace serializedTree
          <JobCatalogLinkedInfoAlert
            jobCatalogSerializedTree={jobCatalogSerializedTree}
            jobCatalogLevelUuid={job.jobCatalogNodeLevelsUuid ?? ''}
            jobCatalogRoleUuid={job.jobCatalogNodeRolesUuid ?? ''}
          />
        )
      }
    >
      <Form onSubmit={() => form.handleSubmit()} padding={null}>
        <Text size="400" color="grey1000" weight="semibold">
          {i18n.t('ats.job_posting_settings.basic_information.basic_info_title')}
        </Text>

        <Box gap="s16" paddingBottom="s24">
          <Input
            name="title"
            label={i18n.t('ats.job_posting_settings.basic_information.job_opening_label')}
            value={form.values.title}
            onChange={(title) => form.setFieldValue('title', title)}
            hint={i18n.t('ats.job_posting_settings.basic_information.job_opening_description')}
            error={form.errors.title}
            size="md"
          />

          {jobCatalogFeatureEnabled && jobCatalogRoles.length > 0 && (
            <JobCatalogRoleSelector
              onLevelSelect={(roleUuid, roleLevelUuid) => {
                form.setValues({
                  ...form.values,
                  jobCatalogNodeRolesUuid: roleUuid ?? null,
                  jobCatalogNodeLevelsUuid: roleLevelUuid ?? null,
                })
              }}
              jobCatalogRoles={jobCatalogRoles}
              selectedRoleUuid={job.jobCatalogNodeRolesUuid ?? undefined}
              selectedRoleLevelUuid={job.jobCatalogNodeLevelsUuid ?? undefined}
            />
          )}

          {(company.legalEntities.length > 1 || !form.values.legalEntityId) && (
            <F0Select
              label={i18n.t('ats.job_posting_settings.basic_information.legal_entity_label')}
              placeholder={i18n.t('common.select_basic')}
              name="legalEntityId"
              value={form.values.legalEntityId?.toString()}
              onChange={(value: string) => form.setFieldValue('legalEntityId', Number(value))}
              options={company.legalEntities.map((entity) => ({
                label: `${entity.legalName} - ${entity.currency}`,
                value: entity.id.toString(),
              }))}
              size="md"
            />
          )}
          <Grid variant="fixed">
            <Box gridColSpan={{ xxs: 1, xs: 6 }}>
              <F0Select
                label={i18n.t('ats.job_posting_settings.basic_information.team_label')}
                placeholder={i18n.t('common.select_basic')}
                name="teamId"
                value={form.values.teamId?.toString()}
                defaultItem={{ label: i18n.t('common.select_basic'), value: '' }}
                onChange={(value: string) => form.setFieldValue('teamId', Number(value))}
                options={teams.map((team) => ({
                  label: team.name,
                  value: team.id.toString(),
                }))}
                size="md"
              />
            </Box>
            <Box gridColSpan={{ xxs: 1, xs: 6 }}>
              <F0Select
                placeholder={i18n.t('common.select_basic')}
                label={i18n.t('ats.job_posting_settings.basic_information.type_of_location_label')}
                name="workplaceType"
                value={form.values.workplaceType ?? ''}
                defaultItem={{ label: i18n.t('common.select_basic'), value: '' }}
                onChange={(value: string) => form.setFieldValue('workplaceType', value)}
                options={[
                  {
                    label: i18n.t('ats.workplace_types.onsite'),
                    value: 'onsite',
                  },
                  {
                    label: i18n.t('ats.workplace_types.hybrid'),
                    value: 'hybrid',
                  },
                  {
                    label: i18n.t('ats.workplace_types.remote'),
                    value: 'remote',
                  },
                ]}
                size="md"
              />
            </Box>
          </Grid>
          <Grid variant="fixed">
            {!isHiringPlanEnabled && (
              <Box gridColSpan={{ xxs: 1, xs: 6 }}>
                <NumberInput
                  label={i18n.t(
                    'ats.job_posting_settings.basic_information.number_of_vacancies_label'
                  )}
                  locale="en-US"
                  name="numberOfVacancies"
                  onChange={(value) =>
                    form.setFieldValue('numberOfVacancies', value === null ? null : Number(value))
                  }
                  size="md"
                  value={form.values.numberOfVacancies}
                  error={form.errors.numberOfVacancies}
                />
              </Box>
            )}
            <Box gridColSpan={{ xxs: 1, xs: 6 }}>
              <F0DatePicker
                label={i18n.t('ats.job_posting_settings.basic_information.start_date_label')}
                onChange={handleStartDateChange}
                size="md"
                value={startDatePickerValue}
              />
            </Box>
          </Grid>
          <Grid variant="fixed">
            <Box gridColSpan={{ xxs: 1, xs: 6 }}>
              <Checkbox
                checked={form.values.allowReferral}
                label={i18n.t('ats.job_details.promotion.open_positions.allow_referral_label')}
                onChange={(event) => form.setFieldValue('allowReferral', event.target.checked)}
              />
            </Box>
            <Box gridColSpan={{ xxs: 1, xs: 6 }}>
              <Checkbox
                checked={form.values.allowInternalApply}
                label={i18n.t(
                  'ats.job_details.promotion.open_positions.allow_internal_apply_label'
                )}
                onChange={(event) => form.setFieldValue('allowInternalApply', event.target.checked)}
              />
            </Box>
          </Grid>
        </Box>
        <Text size="400" color="grey1000" weight="semibold">
          {i18n.t('ats.job_posting_settings.basic_information.location_title')}
        </Text>
        <Box gap="s16" paddingBottom="s24">
          <F0Select
            label={i18n.t('ats.job_posting_settings.basic_information.workplace_label')}
            placeholder={i18n.t('common.select_basic')}
            name="locationId"
            value={form.values.locationId?.toString()}
            defaultItem={{ label: i18n.t('common.select_basic'), value: '' }}
            onChange={(value: string) => onChangeLocation(value)}
            options={locations.map((location) => {
              return {
                label: location.name,
                value: location.id.toString(),
              }
            })}
            error={form.errors.locationId}
            size="md"
          />
          <Grid variant="fixed">
            <Box gridColSpan={{ xxs: 1, xs: 6 }}>
              <Input
                label={i18n.t('ats.job_posting_settings.basic_information.country_label')}
                value={countryName(form.values.country)}
                disabled
                size="md"
              />
            </Box>
            <Box gridColSpan={{ xxs: 1, xs: 6 }}>
              <Input
                label={i18n.t('ats.job_posting_settings.basic_information.state_label')}
                value={form.values.state}
                disabled
                size="md"
              />
            </Box>
            <Box gridColSpan={{ xxs: 1, xs: 6 }}>
              <Input
                label={i18n.t('ats.job_posting_settings.basic_information.city_label')}
                value={form.values.city}
                disabled
                size="md"
              />
            </Box>
            <Box gridColSpan={{ xxs: 1, xs: 6 }}>
              <Input
                label={i18n.t('ats.job_posting_settings.basic_information.zip_code_label')}
                value={form.values.postalCode}
                disabled
                size="md"
              />
            </Box>
          </Grid>
        </Box>

        <F0Alert
          variant="warning"
          title={i18n.t('ats.jobs_form.workplace_warning_message')}
          description=""
        />

        {!form.isValid && (
          <F0Alert
            variant="warning"
            title={i18n.t('ats.jobs_form.missing_fields_warning')}
            description=""
          />
        )}

        <Box maxWidth="fit-content">
          <F0Button
            loading={form.isSubmitting}
            disabled={!form.isValid}
            type="submit"
            label={i18n.t('common.submit')}
          />
        </Box>
      </Form>
    </SectionComponentLayout>
  )
}
